// React i hooks
import { memo, useEffect, useState } from "react";

// Tipovi i konstante
import { AnnualTaxEditProps } from "./types";
import { Errors } from "services/forms/validation/types";
import { AnnualTaxEditFormValues } from "services/api/calculations/annualtax/types";
import { CALCULATION_ANNUAL_TAX_EDIT_FORM_SCHEMA } from "./validation";
import { initialAnnualTaxEditForm } from "services/api/calculations/annualtax/consts";
import {
  DataWithPagination,
  initialPagination,
} from "ui/components/Table/consts";
import { initialDataWithPagination } from "services/api/consts";

// API servisi
import {
  createAnnualTaxAPI,
  getAnnualTaxAPI,
  updateAnnualTaxAPI,
} from "services/api/calculations/annualtax/api";
import { getVehicleGroupsAPI } from "services/api/calculations/vehiclegroup/api";

// UI komponente
import { Button } from "ui/components/Button/Button";
import { TextField } from "ui/components/TextField/TextField";
import { Loader } from "ui/components/Loader";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";

// Form hooks i validacija
import { validateYup } from "services/forms/validation/validation";
import {
  useHandleDropdownChange,
  useHandleTextFieldChange,
} from "services/forms/hooks";

// Helperi
import { useTranslate } from "helpers";

// Tipovi podataka
import { VehicleGroupRow } from "services/api/calculations/vehiclegroup/types";

const AnnualTaxEditCard: React.FC<AnnualTaxEditProps> = (props) => {
  const { annualTaxId, onSuccess } = props;

  const { t } = useTranslate();
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [vehicleGroups, setVehicleGroups] = useState<
    DataWithPagination<VehicleGroupRow>
  >(initialDataWithPagination);

  const [formValues, setFormValues] = useState<AnnualTaxEditFormValues>(
    initialAnnualTaxEditForm
  );

  useEffect(() => {
    if (annualTaxId) {
      fetchAnnualTax(annualTaxId);
      fetchVehicleGroups();
    }
  }, [annualTaxId]);

  const fetchAnnualTax = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialAnnualTaxEditForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getAnnualTaxAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);

    setIsLoading(false);

    setErrors({});
  };

  const fetchVehicleGroups = async () => {
    setIsLoading(true);

    let result;

    try {
      result = await getVehicleGroupsAPI(initialPagination);
    } catch {
      setIsLoading(false);
      return;
    }

    setVehicleGroups(result);
    setIsLoading(false);
  };

  const handleFormSubmit = async (e: any) => {
    e.preventDefault();

    const isValid = validateYup(
      formValues,
      CALCULATION_ANNUAL_TAX_EDIT_FORM_SCHEMA,
      setErrors
    );

    if (!isValid) {
      return;
    }

    setIsLoading(true);

    try {
      let result;
      if (!!annualTaxId) {
        result = await updateAnnualTaxAPI(annualTaxId, formValues);
      } else {
        result = await createAnnualTaxAPI(formValues);
      }
      if (result && onSuccess) onSuccess();
    } catch (e) {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
  };

  const handleTextFieldChange = useHandleTextFieldChange(
    setFormValues,
    formValues
  );

  const handleDropdownChange = useHandleDropdownChange(
    setFormValues,
    formValues
  );

  const vehicleGroupItems: DropdownOption[] = vehicleGroups.data.map((i) => {
    return {
      value: i.id.toString(),
      text: i.name,
    };
  });

  return (
    <>
      <div className="flex gap-4">
        <div className="w-full m-auto">
          <div className="space-y-3">
            <form noValidate className="text-left">
              <div className="px-5 pb-3 mb-2">
                <div className="flex flex-row gap-3 mb-3">
                  <div className="flex-1 ">
                    <TextField
                      type="text"
                      name="fromKw"
                      label={t(
                        "admin.calculation.setting.annualtax.add.fromkw"
                      )}
                      placeholder={t(
                        "admin.calculation.setting.annualtax.add.fromkw.placeholder"
                      )}
                      onChange={handleTextFieldChange}
                      error={errors.fromKw}
                      value={formValues.fromKw}
                      required
                      endIcon=""
                      groupAppend="kw"
                    ></TextField>
                  </div>
                  <div className="flex-1 ">
                    <TextField
                      type="text"
                      name="toKw"
                      label={t("admin.calculation.setting.annualtax.add.tomkw")}
                      placeholder={t(
                        "admin.calculation.setting.annualtax.add.tomkw.placeholder"
                      )}
                      onChange={handleTextFieldChange}
                      error={errors.toKw}
                      value={formValues.toKw}
                      required
                      endIcon=""
                      groupAppend="kw"
                    ></TextField>
                  </div>
                </div>
                <div className="flex gap-3 mb-3">
                  <div className="flex-1 ">
                    <TextField
                      type="text"
                      name="price"
                      label={t("admin.calculation.setting.annualtax.add.price")}
                      placeholder={t(
                        "admin.calculation.setting.annualtax.add.price.placeholder"
                      )}
                      onChange={handleTextFieldChange}
                      error={errors.price}
                      value={formValues.price}
                      required
                      endIcon=""
                      groupAppend="€"
                    ></TextField>
                  </div>
                </div>
              </div>
              <div className="px-5 pb-5 flex justify-end ">
                <Button
                  variant="primary"
                  size="full"
                  children={t("admin.save")}
                  onClick={handleFormSubmit}
                />
              </div>
            </form>
          </div>
        </div>
      </div>

      <Loader isLoading={isLoading ?? false}> </Loader>
    </>
  );
};
export default memo(AnnualTaxEditCard);
